import { alpha, createTheme, darken } from '@mui/material'

// Color setup
const mainColor = '#FFD700' // Gold color for a celebratory feel

const darkBackground = '#121212'
const darkPrimaryMain = '#FFD700'
const darkPrimaryLight = '#FFEA00'
const darkSecondary = '#FFC107'
const darkError = '#FF4081'
const darkWarning = '#FFA000'
const darkInfo = '#448AFF'
const darkSuccess = '#69F0AE'

const lightBackground = '#FFFFFF'
const lightPrimaryMain = '#FFA726'
const lightPrimaryLight = '#FFB74D'
const lightSecondary = '#FF8A65'
const lightError = '#E57373'
const lightWarning = '#FFB74D'
const lightInfo = '#64B5F6'
const lightSuccess = '#81C784'
//

export const logInBackgroundImage = `linear-gradient(135deg, ${lightPrimaryMain} 0%, ${lightSecondary} 100%)`

const darkPalette = {
  background: { default: darkBackground },
  primary: { main: darkPrimaryMain, light: darkPrimaryLight },
  secondary: { main: darkSecondary },
  error: { main: darkError },
  warning: { main: darkWarning },
  info: { main: darkInfo },
  success: { main: darkSuccess },
  mode: 'dark',
}

const lightPalette = {
  background: { default: lightBackground },
  primary: { main: lightPrimaryMain, light: lightPrimaryLight },
  secondary: { main: lightSecondary },
  error: { main: lightError },
  warning: { main: lightWarning },
  info: { main: lightInfo },
  success: { main: lightSuccess },
  text: {
    primary: '#333333',
    secondary: '#555555',
  },
  mode: 'light',
}

const componentsOverrides = (theme) => ({
  MuiBackdrop: {
    styleOverrides: {
      root: {
        backgroundColor: alpha(darken('#000000', 0.4), 0.2),
        backdropFilter: 'blur(2px)',
        '&.MuiBackdrop-invisible': {
          backgroundColor: 'transparent',
          backdropFilter: 'blur(2px)',
        },
      },
    },
  },
  MuiFormControl: {
    defaultProps: {
      margin: 'dense',
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      input: {
        padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: {
        padding: 0,
        height: 38,
        minHeight: 38,
        borderRadius: 6,
        transition: 'color .2s',
        '&.MuiButtonBase-root': {
          minWidth: 'auto',
          paddingLeft: 20,
          paddingRight: 20,
          marginRight: 4,
        },
        '&.Mui-selected, &.Mui-selected:hover': {
          color: theme.palette.primary.contrastText,
          zIndex: 5,
        },
        '&:hover': {
          color: theme.palette.primary.main,
        },
      },
    },
  },
  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:last-child td': { border: 0 },
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: theme.spacing(2),
        '&.MuiTableCell-sizeSmall': {
          padding: theme.spacing(1.5),
        },
        '&.MuiTableCell-paddingNone': {
          padding: theme.spacing(0.5),
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        height: 38,
        minHeight: 38,
        overflow: 'visible',
      },
      indicator: {
        height: 38,
        minHeight: 38,
        borderRadius: 6,
        border: `1px solid ${theme.palette.primary.light}`,
        boxShadow: theme.shadows[1],
      },
      scrollableX: {
        overflow: 'visible !important',
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      variant: 'outlined',
    },
  },
  RaAppBar: {
    styleOverrides: {
      root: {
        color: theme.palette.text.primary,
        '& .RaAppBar-toolbar': {
          backgroundColor: theme.palette.primary.main,
          color: theme.palette.background.default,
          backgroundImage: `linear-gradient(310deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
        },
      },
    },
  },
  RaMenuItemLink: {
    styleOverrides: {
      root: {
        padding: 10,
        marginRight: 10,
        marginLeft: 10,
        '&:hover': {
          borderRadius: 5,
        },
        '&.RaMenuItemLink-active': {
          borderRadius: 10,
          backgroundColor: theme.palette.common.white,
          color: theme.palette.primary.main,
          '&:before': {
            content: '""',
            position: 'absolute',
            top: '0; right: 0; bottom: 0; left: 0',
            zIndex: '-1',
            margin: '-2px',
            borderRadius: '12px',
            background: `linear-gradient(310deg, ${theme.palette.primary.light}, ${theme.palette.secondary.main})`,
          },
          '& .MuiSvgIcon-root': {
            fill: theme.palette.primary.main,
          },
        },
      },
    },
  },
})

const createCustomTheme = (palette) => {
  const themeOptions = {
    palette,
    shape: { borderRadius: 10 },
    sidebar: { width: 250, closedWidth: 65 },
    spacing: 9,
    typography: {
      fontFamily: 'Gabarito, tahoma, sans-serif',
      h1: {
        fontWeight: 500,
        fontSize: '6rem',
      },
      h2: { fontWeight: 600 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 800 },
      h5: { fontWeight: 900 },
      button: { textTransform: undefined, fontWeight: 700 },
    },
  }
  const theme = createTheme(themeOptions)
  theme.components = componentsOverrides(theme)
  return theme
}

export const customLightTheme = createCustomTheme(lightPalette)
export const customDarkTheme = createCustomTheme(darkPalette)
